//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from 'src/app/common/service';
import FarmTeleHealthModel from './farmTeleHealth.model';

/**
 * @service FarmTeleHealthService
 * @description Service for managing farm telehealth and chat communication operations.
 * Provides HTTP methods for retrieving messages, unread counts, and attachments between users
 * (e.g., SMEs, Farmers, Technicians) within the AquaCloud ecosystem.
 * Extends base Service class with generic type FarmTeleHealthModel.
 *
 * ## Key Responsibilities:
 * 1. **Message Retrieval**: Fetches chat history between users with pagination support.
 * 2. **Unread Counts**: Retrieves unread message counts for specific users or groups.
 * 3. **Attachment Management**: Fetches media attachments exchanged in chat sessions.
 * 4. **Request Management**: Tracks and cancels ongoing HTTP requests to prevent memory leaks.
 *
 * ## Features:
 * - Real-time-like message history retrieval via REST API.
 * - Support for thumbnail and text content in messages.
 * - Pagination for chat history to optimize performance.
 * - Attachment retrieval for media sharing.
 *
 * @architecture
 * Extends base Service<FarmTeleHealthModel> providing REST API integration with configurable base URL
 * and service endpoint. Implements request tracking for cleanup and cancellation. Uses HttpClient
 * for HTTP operations with standard error handling.
 *
 * @service-endpoint /farmtelehealth/
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class FarmTeleHealthService extends Service<FarmTeleHealthModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions.
   * Used to store subscription references for cancellation via cancelOnGoingHttpRequests().
   * @default []
   * @side-effect Modifies the array when requests are made or cancelled.
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient.
   *
   * Initializes service by calling parent Service constructor with baseUrl
   * configuration and service endpoint path.
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests.
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/farmtelehealth/');
  }

  /**
   * @method getUnreadMessages
   * @async
   * @description Fetch unread message counts for a list of person IDs.
   *
   * Makes an HTTP POST request to the backend endpoint `/farmtelehealth/messages/lastmessages/unreadcount`.
   * Sends a payload containing person IDs to retrieve their unread message status.
   *
   * @param {FarmTeleHealthModel} personIds - Model containing the list of person IDs to check.
   * @returns {Promise<Array<FarmTeleHealthModel>>} Promise resolving to array of FarmTeleHealthModel objects with unread counts.
   * @throws {HttpErrorResponse} Rejects promise with HttpErrorResponse if request fails.
   * @side-effect
   * - Creates HTTP POST request and stores subscription reference in ongoingRequests.
   * - Subscription can be cancelled via cancelOnGoingHttpRequests().
   *
   * @example
   * const req = new FarmTeleHealthModel();
   * req.personIDs = ['id1', 'id2'];
   * const unreadData = await farmTeleHealthService.getUnreadMessages(req);
   */
  public getUnreadMessages(personIds: FarmTeleHealthModel) {
    return new Promise<Array<FarmTeleHealthModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<FarmTeleHealthModel>>(`${this.baseUrl}${this.serviceUrl}messages/lastmessages/unreadcount`, personIds, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getMessagesBySenderIdReceiverId
   * @async
   * @description Fetch chat message history between the current user and a receiver.
   *
   * Makes an HTTP GET request to `/farmtelehealth/messages` with query parameters for pagination,
   * sorting, and content inclusion (thumbnails, text).
   *
   * @param {string} receiverId - The ID of the user receiving messages (or the other party in chat).
   * @param {number} page - The page number for pagination (0-indexed).
   * @returns {Promise<Array<FarmTeleHealthModel>>} Promise resolving to array of message objects.
   *
   * @query-params
   * - thumbnail: true
   * - text: true
   * - receiverID: {receiverId}
   * - history: true
   * - order: new
   * - page: {page}
   * - limit: 10
   *
   * @side-effect
   * - Creates HTTP GET request and stores subscription reference in ongoingRequests.
   *
   * @example
   * const messages = await farmTeleHealthService.getMessagesBySenderIdReceiverId('receiver-123', 0);
   */
  public getMessagesBySenderIdReceiverId(receiverId: string, page: number) {
    return new Promise<Array<FarmTeleHealthModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<FarmTeleHealthModel>>(`${this.baseUrl}${this.serviceUrl}messages?thumbnail=true&text=true&receiverID=${receiverId}&history=true&order=new&page=${page}&limit=10`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getAttachmentsBySenderIdReceiverId
   * @async
   * @description Fetch all media attachments exchanged between a sender and receiver.
   *
   * Makes an HTTP POST request to `/farmtelehealth/messages/getAttachments/senderId/receiverId`.
   *
   * @param {string} senderId - The ID of the sender (usually the other party in the chat context).
   * @returns {Promise<Array<FarmTeleHealthModel>>} Promise resolving to array of attachment objects.
   * @side-effect
   * - Creates HTTP POST request and stores subscription reference in ongoingRequests.
   *
   * @example
   * const attachments = await farmTeleHealthService.getAttachmentsBySenderIdReceiverId('sender-456');
   */
  public getAttachmentsBySenderIdReceiverId(senderId: string) {
    return new Promise<Array<FarmTeleHealthModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<FarmTeleHealthModel>>(`${this.baseUrl}${this.serviceUrl}messages/getAttachments/senderId/receiverId`, {
        'senderID': senderId
      }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  //To Cancel the onGoing Http Requests
  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks.
   *
   * Iterates through ongoingRequests array and unsubscribes from each active
   * subscription. Clears the requests array after unsubscribing from all.
   *
   * @execution-flow
   * 1. Iterate through each subscription in ongoingRequests array.
   * 2. Check if subscription has unsubscribe function.
   * 3. Call unsubscribe() on each active subscription.
   * 4. Clear ongoingRequests array.
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests.
   * - Clears ongoingRequests array.
   *
   * @usage Called during component ngOnDestroy to cleanup subscriptions.
   * @example
   * ngOnDestroy() {
   *   this.farmTeleHealthService.cancelOnGoingHttpRequests();
   * }
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
